import React, { useMemo } from 'react';
import { useSelectedElementId, useSelectedVersion } from '@client/store/ui';
import { useProjectState } from '@client/store/project';
import { useResolvedElement } from '@client/hooks/use-resolved-version.hooks';
import { projectGuards } from '@shared/helpers/guards/project-guards.helpers';
import { ElementSidePanelContent } from '@client/pages/MainView/components/SidePanel/components/ElementSidePanelContent';
import { useElementImports } from './use-element-imports.hook';
import { ImportSidePanel } from './ImportSidePanel';
import { useImportedElements } from './candidate-status.helpers';

/**
 * Side-panel content for the CSV import tab. Resolves the selection (which may
 * be a candidate-row id or, once imported, the materialized element id) and:
 *   - import group header → {@link ImportSidePanel}
 *   - imported row → standard element panel
 *   - not-yet-imported row → no side-panel content (mapping lives in list header)
 */
export const CandidateRowSidePanel: React.FC = () => {
  const selectedId = useSelectedElementId();
  const { project } = useProjectState('project');
  const imports = useElementImports(project?.id ?? -1);
  const version = useSelectedVersion();
  const resolvedSelectedElement = useResolvedElement(selectedId);

  const selectedImport =
    imports.status === 'ready' && selectedId
      ? imports.data.importsById[selectedId]
      : undefined;

  // The live (in-tree) element behind the selection: either the selection
  // resolves directly to a tree element (URL points at the materialized
  // element), or the selection is the row id whose imported element we look up
  // by origin.candidateRowId.
  const importedElements = useImportedElements(version);
  const liveElement = useMemo(() => {
    if (selectedImport || !selectedId) return undefined;
    if (
      projectGuards.is.element(resolvedSelectedElement) &&
      resolvedSelectedElement.origin?.kind === 'csvImport'
    ) {
      // Only an import in this proposal's view; another proposal's is not.
      const inView = importedElements.get(
        resolvedSelectedElement.origin.candidateRowId,
      );
      return inView?.id === resolvedSelectedElement.id
        ? resolvedSelectedElement
        : undefined;
    }
    return importedElements.get(selectedId);
  }, [selectedImport, selectedId, resolvedSelectedElement, importedElements]);

  if (!project?.id || imports.status !== 'ready') return null;

  if (selectedImport) {
    return <ImportSidePanel imp={selectedImport} projectId={project.id} />;
  }
  if (!liveElement) return null;

  return <ElementSidePanelContent selectedElement={liveElement} />;
};
